import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { catchError, map, Observable, of, shareReplay, tap, throwError } from 'rxjs';
import { Event } from '../domain/registrator';
import { MessageService } from './message.service';

const CACHE_SIZE = 1;


@Injectable({
  providedIn: 'root'
})
export class EventService {
  endpoint: string = environment.api_url;
  headers = new HttpHeaders().set('Content-Type', 'application/json');
  private cache$!: Observable<Array<Event>>;
  private cacheDirty: boolean = true;

  constructor(private http: HttpClient, private messageService: MessageService) { }

  private requestEvents() {
    console.debug('requestEvents');
    return this.http.get<Event[]>(`${this.endpoint}/events`, {})
      .pipe(
        // Map the response array, converting date strings to Date objects.
        map((response: Event[]) => {
          return response.map(item => new Event(
            item.id,
            item.name,
            item.shortName,
            item.pageTitle,
            item.headerText,
            item.invoicePrefix,
            item.date ? item.date : null,
            item.endDate ? item.endDate : null,
            item.url,
            item.location,
            item.shortLocation,
            item.logoFileName,
            item.freeEvent,
            item.allowMultipleRegistrations,
            item.usesPromoCodes,
            item.oneFreeFor,
            item.vatRate,
            item.earlyBirdDeadline ? item.earlyBirdDeadline : null,
            item.registrationOpenDate ? item.registrationOpenDate : null,
            item.registrationCloseDate ? item.registrationCloseDate : null,
            item.fees,
            item.disclaimers,
            item.paymentConditions,
            item.contactEmail,
            item.hotelBookingUrl,
            item.optionGroups
          ))
        }),
        tap((events: Event[]) => { // The tap operator now receives the converted Event[]
          console.debug('Events fetched and dates converted:', events);
          this.cacheDirty = false;
        }),
        catchError(this.handleError<Event[]>('requestEvents', [])),
      )
  }

  public refreshCache() {
    if (this.cache$ || this.cacheDirty) {
      this.cache$ = this.requestEvents().pipe(
        shareReplay(CACHE_SIZE),
      );
    }
  }

  public deleteCache() {
    this.cacheDirty = true;
  }

  get events() {
    if (!this.cache$ || this.cacheDirty) {
      this.cache$ = this.requestEvents().pipe(
        shareReplay(CACHE_SIZE),
      );
    }
    return this.cache$;
  }

  private toEvent(item: any): Event {
    return new Event(
      item.id,
      item.name,
      item.shortName,
      item.pageTitle,
      item.headerText,
      item.invoicePrefix,
      item.date ? item.date : null,
      item.endDate ? item.endDate : null,
      item.url,
      item.location,
      item.shortLocation,
      item.logoFileName,
      item.freeEvent,
      item.allowMultipleRegistrations,
      item.usesPromoCodes,
      item.oneFreeFor,
      item.vatRate,
      item.earlyBirdDeadline ? item.earlyBirdDeadline : null,
      item.registrationOpenDate ? item.registrationOpenDate : null,
      item.registrationCloseDate ? item.registrationCloseDate : null,
      item.fees,
      item.disclaimers,
      item.paymentConditions,
      item.contactEmail,
      item.hotelBookingUrl,
      item.optionGroups
    );
  }

  getEventByName(name: string): Observable<Event> {
    return this.http.get<Event>(`${this.endpoint}/events/${name}`).pipe(
      map((item: Event) => this.toEvent(item)),
      catchError(this.handleError<Event>('getEventByName', undefined))
    );
  }

  createEvent(event: any): Observable<Event> {
    return this.http.post<Event>(`${this.endpoint}/events/`, event, { headers: this.headers }).pipe(
      map((item: Event) => this.toEvent(item)),
      tap(() => this.deleteCache()),
      catchError(err => {
        console.error('createEvent failed', err);
        return throwError(() => err);
      })
    );
  }

  updateEvent(eventShortName: string, event: any): Observable<Event> {
    return this.http.put<Event>(`${this.endpoint}/events/${eventShortName}`, event, { headers: this.headers }).pipe(
      map((item: Event) => this.toEvent(item)),
      tap(() => this.deleteCache()),
      catchError(err => {
        console.error('updateEvent failed', err);
        return throwError(() => err);
      })
    );
  }

  getParticipantsList(name: string, filter?: string): Observable<HttpResponse<ArrayBuffer>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    // const statuses = Object.keys(RegistrationStatus)
    //   .filter(key => isNaN(Number(key)) && key !== 'DELETED'); // Exclude 'DELETED' and numeric keys
    // const params = new HttpParams().set('statusFilter', statuses.join(','));

    const params = {}

    return this.http.get(`${this.endpoint}/events/${name}/participantslist`, {
      params,
      headers,
      responseType: 'arraybuffer' as 'arraybuffer',
      observe: 'response'
    });
  }

  eventExists(name: string): Observable<boolean> {
    return this.http.head(`${this.endpoint}/events/${name}`, { observe: 'response' }).pipe(
      map(response => response.status === 200),
      catchError(error => {
        if (error.status === 404) {
          return of(false);
        } else {
          console.error('Error checking event existence:', error);
          return of(false); // or handle the error as needed
        }
      })
    );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: ${error.message}`);
      this.messageService.showMessage('Can\'t load the event', true);
      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
